import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateFn, Router} from '@angular/router';

import {Observable, forkJoin, map, switchMap, tap} from 'rxjs';

import {AppId, AppRoutes, RootAppUrl, RouteData} from 'frontend/constants/routes';
import {BaseConfigService, CONFIG_SERVICE_TOKEN} from 'frontend/services/base_config_service';
import {LegalConsentService} from 'frontend/services/legal_consent_service';
import {Organization} from 'frontend/utils/organization';

/**
 * Shared prefetch pipeline: fetches user/org config and resolves legal consent.
 */
function prefetchAccessData(
  configService: BaseConfigService,
  legalConsentService: LegalConsentService,
): Observable<void> {
  return forkJoin([
    configService.userScope.fetchUserConfig(),
    configService.userScope.fetchOrganizationConfig(),
  ]).pipe(
    switchMap(() => legalConsentService.shouldPromptConsent()),
    tap((promptConsent) => {
      legalConsentService.consentRequired.set(promptConsent);
    }),
    map(() => undefined),
  );
}

/**
 * Checks if the current user has access to a route based on their organization
 * and the target application path segment. If access is denied and a default landing page is available
 * for the organization, it redirects to that path. Otherwise, it redirects to the static
 * AccessDenied page.
 */
export const canUserAccessRoute: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const configService = inject(CONFIG_SERVICE_TOKEN);
  const legalConsentService = inject(LegalConsentService);

  return prefetchAccessData(configService, legalConsentService).pipe(
    map(() => {
      const {appPathIdentifier} = route.data as RouteData;

      if (!appPathIdentifier) {
        return router.createUrlTree([AppRoutes.ACCESS_DENIED.path]);
      }

      const {hasAccess, defaultPath} = resolveRouteAccess(
        configService.getOrganization(),
        appPathIdentifier,
      );

      if (hasAccess) {
        return true;
      }

      return router.createUrlTree([defaultPath || AppRoutes.ACCESS_DENIED.path]);
    }),
  );
};

/**
 * Guard for the Access Denied route (/access-denied).
 * Redirects authorized users with a valid default landing page away to their app.
 */
export const canUserAccessDeniedRoute: CanActivateFn = () => {
  const router = inject(Router);
  const configService = inject(CONFIG_SERVICE_TOKEN);
  const legalConsentService = inject(LegalConsentService);

  return prefetchAccessData(configService, legalConsentService).pipe(
    map(() => {
      const organization = configService.getOrganization();
      const {defaultPath} = resolveRouteAccess(organization, AppRoutes.ACCESS_DENIED.app);

      if (defaultPath && defaultPath !== AppRoutes.ACCESS_DENIED.path) {
        return router.createUrlTree([defaultPath]);
      }

      return true;
    }),
  );
};

/**
 * Determines if a specific app is accessible for a given organization and provides the
 * organization's default landing path.
 */
function resolveRouteAccess(
  organization: Organization | null | undefined,
  appToActivate: AppId,
): {hasAccess: boolean; defaultPath?: RootAppUrl} {
  switch (organization) {
    case Organization.GOOGLE:
    case Organization.PLAYWRIGHT_PLATFORM:
      return {
        hasAccess: true,
        defaultPath: AppRoutes.GPT.path,
      };

    case Organization.AES:
    case Organization.CEN:
    case Organization.NORD:
      return {
        hasAccess: hasAppIdentifier(appToActivate, [AppRoutes.GPT.app, AppRoutes.SIMULATIONS.app]),
        defaultPath: AppRoutes.GPT.path,
      };
    case Organization.AES_US:
      return {
        hasAccess: hasAppIdentifier(appToActivate, [
          AppRoutes.GPT.app,
          AppRoutes.SIMULATIONS.app,
          AppRoutes.MODEL_BUILDER.app,
        ]),
        defaultPath: AppRoutes.GPT.path,
      };
    case Organization.AES_ANDES:
    case Organization.CNE:
    case Organization.PLAYWRIGHT_GPT_T:
    case Organization.PLAYWRIGHT_UMM:
      return {
        hasAccess: hasAppIdentifier(appToActivate, [AppRoutes.GPT.app]),
        defaultPath: AppRoutes.GPT.path,
      };
    case Organization.VECTOR:
      return {
        hasAccess: hasAppIdentifier(appToActivate, [AppRoutes.GPTD.app]),
        defaultPath: AppRoutes.GPTD.path,
      };
    case Organization.PJM:
    case Organization.PJM_TO:
      return {
        hasAccess: hasAppIdentifier(appToActivate, [
          AppRoutes.MODEL_BUILDER.app,
          AppRoutes.HYPERQUEUE.app,
        ]),
        defaultPath: AppRoutes.MODEL_BUILDER.path,
      };
    case Organization.GOOGLE_RTO:
    case Organization.PLAYWRIGHT_MODEL_WEAVER_TO:
    case Organization.PLAYWRIGHT_MODEL_WEAVER_RTO:
      return {
        hasAccess: hasAppIdentifier(appToActivate, [AppRoutes.MODEL_BUILDER.app]),
        defaultPath: AppRoutes.MODEL_BUILDER.path,
      };
    // Specifically used for Playwright E2E tests, not for real users.
    // TODO(tenghanzhi): Consider migrating per-app PLAYWRIGHT organizations to a single generic
    // PLAYWRIGHT organization and using E2E test fixtures (beforeEach) to configure app-specific
    // geographic and viewport settings dynamically.
    case Organization.PLAYWRIGHT_GPTD:
      return {
        hasAccess: hasAppIdentifier(appToActivate, [AppRoutes.GPTD.app]),
        defaultPath: AppRoutes.GPTD.path,
      };
    case Organization.PLAYWRIGHT_GRID_READY:
      return {
        hasAccess: hasAppIdentifier(appToActivate, [AppRoutes.HOSTING_CAPACITY.app]),
        defaultPath: AppRoutes.HOSTING_CAPACITY.path,
      };
    case Organization.PLAYWRIGHT_HYPERQ:
      return {
        hasAccess: hasAppIdentifier(appToActivate, [AppRoutes.HYPERQUEUE.app]),
        defaultPath: AppRoutes.HYPERQUEUE.path,
      };
    case Organization.PLAYWRIGHT_SIMULATIONS:
      return {
        hasAccess: hasAppIdentifier(appToActivate, [AppRoutes.SIMULATIONS.app]),
        defaultPath: AppRoutes.SIMULATIONS.path,
      };
    default:
      return {
        hasAccess: false,
        defaultPath: AppRoutes.ACCESS_DENIED.path,
      };
  }
}

function hasAppIdentifier(targetAppIdentifier: AppId, appIdentifiers: AppId[]): boolean {
  return appIdentifiers.some(
    (appPathIdentifier: AppId) => appPathIdentifier === targetAppIdentifier,
  );
}
